import CheckoutBasketItem from "../Checkout/CheckoutBasketItem.js";
import OrderableTypes from "../Orders/OrderableTypes.js";
import * as TicketSeatReservationModes from "../Tickets/TicketSeatReservationModes.js";

/**
 * Seat reservation utility methods
 *
 * @package HOPS
 * @subpackage SeatReservation
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class SeatReservationUtils {

	/**
	 * Get the compound (string) journey ID to use for a reservation 
	 * between two points on a particular train asset's route.
	 *
	 * @param {Integer} trainAssetId
	 * @param {?Integer} departureTrainScheduleId
	 * @param {?Integer} arrivalTrainScheduleId
	 * @return {String}
	 */
	static getJourneyId(trainAssetId, departureTrainScheduleId, arrivalTrainScheduleId) {
		return [arrivalTrainScheduleId, departureTrainScheduleId, trainAssetId].filter(id => id).join(".");
	}


	/**
	 * Deconstruct a journey ID string produced by `getJourneyId()` 
	 * to its constituent components.
	 *
	 * @param {String} journeyId
	 * @return {Object{ArrivalTrainScheduleId: ?int, DepartureTrainScheduleId: ?int, TrainAssetId: int}}
	 */
	static deconstructJourneyId(journeyId) {

		/**
		 * We have to use reverse order because the train schedule 
		 * IDs might not be present at all (see `journeyId`)!
		 */
		const [
			TrainAssetId,
			DepartureTrainScheduleId,
			ArrivalTrainScheduleId
		] = journeyId.split(".").reverse();

		/**
		 * Transform the values!
		 */
		return {
			ArrivalTrainScheduleId: (parseInt(ArrivalTrainScheduleId) || null),
			DepartureTrainScheduleId: (parseInt(DepartureTrainScheduleId) || null),
			TrainAssetId: parseInt(TrainAssetId)
		};

	}


	/**
	 * Get the compound (string) journey ID to use for a reservation 
	 * between two points on a particular train's route.
	 *
	 * @param {Integer} trainAssetId
	 * @param {?Integer} departureTrainScheduleId
	 * @param {?Integer} arrivalTrainScheduleId
	 * @return {String}
	 */
	static getTrainJourneyId(trainId, departureTrainScheduleId, arrivalTrainScheduleId) {
		return [arrivalTrainScheduleId, departureTrainScheduleId, trainId].filter(id => id).join(".");
	}


	/**
	 * Get the number of seats selectable for a basket item.
	 * 
	 * @param {Object} basketItem CheckoutBasketItem-like object
	 * @param {Boolean} allJourneys optional Get the total seats across all journeys (`true`)
	 * @return {Integer}
	 */
	static getSeatCountForBasketItem(basketItem, allJourneys=true) {
		const journeyCount = CheckoutBasketItem.getJourneys(basketItem, false).length;
		return (CheckoutBasketItem.getTargetPeopleCount(basketItem) * basketItem.Quantity * (allJourneys ? journeyCount : 1));
	}


	/**
	 * Get the number of seats allocated for a basket item.
	 * 
	 * @param {Object} basketItem CheckoutBasketItem-like object
	 * @param {Array<Object>} basketItems CheckoutBasketItem-like objects
	 * @returns {Integer}
	 */
	static getAllocatedSeatsForBasketItem(basketItem, basketItems) {
		return basketItems.filter(i => {
			return ((i.OrderableType === OrderableTypes.SeatReservation) &&
					(i.RelatedItemUuid === basketItem.Uuid));
		});
	}


	/**
	 * Validate each item in a collection of basket items to determine 
	 * whether there's a permissible set of seat reservations for it 
	 * within the collection.
	 *
	 * @param {Array<Object>} basketItems CheckoutBasketItem-like objects
	 */
	static validateBasketSelection(basketItems) {

		const results = {};

		for (const item of basketItems) {
			results[item.Uuid] = this.validateBasketItemSelection(item, basketItems);
		}

		return results;

	}


	/**
	 * Validate whether a collection of basket items contains a 
	 * permissible set of seat reservations for a given basket item.
	 *
	 * @param {Object} basketItem CheckoutBasketItem-like object
	 * @param {Array<Object>} basketItems CheckoutBasketItem-like objects
	 * @return {Boolean|undefined} `undefined` when `basketItem` doesn't support reservations
	 */
	static validateBasketItemSelection(basketItem, basketItems) {

		/**
		 * Get the seat reservation mode
		 */
		const seatReservationMode = CheckoutBasketItem.getSeatReservationMode(basketItem);

		/**
		 * We're only interested in items which support reservations
		 */
		if (TicketSeatReservationModes.allowsCustomerSelection(seatReservationMode) || TicketSeatReservationModes.allowsRailwaySelection(seatReservationMode)) {

			/**
			 * Is seat selection mandatory for this item?
			 */
			const isMandatorySelection = (seatReservationMode === TicketSeatReservationModes.values.CustomerSelectionMandatory);

			/**
			 * Get the number of seats we expect for this item
			 */
			const seatCount = this.getSeatCountForBasketItem(basketItem);

			/**
			 * Find seat reservations for this item
			 */
			const seatItems = this.getAllocatedSeatsForBasketItem(basketItem, basketItems);

			/**
			 * Are we valid?
			 */
			return ((seatItems.length === seatCount) || (!isMandatorySelection && (seatItems.length <= seatCount)));

		}

		/**
		 * No reservation supported = no meaningful response
		 */
		else return undefined;

	}

}

export default SeatReservationUtils;
